import {
  pricePredictionsFinalQueryKey,
  pricePredictionsQueryKey,
  usePricePredictions,
} from "../../hooks/pricePredictions.hooks";
import { useCallback, useEffect, useState } from "react";
import { getErrorMessage } from "common/helpers/error.helpers";
import { showToastError } from "common/toast/toast";
import { useQueryClient } from "react-query";
import { ContentCard } from "../../components/ContentCard";
import {
  Button,
  Checkbox,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AppraiserProperty,
  PropertyRequestStatus,
} from "../../types/appraiser.types";
import { PredictionOverviewCard } from "../../components/predictionOverview/PredictionOverviewCard";
import { useRouteQuery } from "../../hooks/router.hooks";
import colors from "common/styles/colors";

interface PropertyQuickValuationSummaryProps {
  property: AppraiserProperty;
  isSticky?: boolean;
}

export const PropertyQuickValuationSummary: React.FC<
  PropertyQuickValuationSummaryProps
> = ({ property, isSticky }) => {
  const [price, setPrice] = useState<number | null>(null);
  const [isAgreed, setIsAgreed] = useState(false);
  const { qSetSimpleQuickPropertyValue } = usePricePredictions(
    property._id,
    true
  );
  const query = useRouteQuery();

  const token = query.get("token");

  const queryClient = useQueryClient();

  const isSubmitDisabled = !!(
    !price ||
    price < 1 ||
    !isAgreed ||
    !property?.area ||
    property?.propertyRequestStatus === PropertyRequestStatus.Completed ||
    qSetSimpleQuickPropertyValue.isLoading ||
    qSetSimpleQuickPropertyValue.isSuccess
  );

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries(["appraiserProperties"]);
    queryClient.invalidateQueries([pricePredictionsQueryKey]);
    queryClient.invalidateQueries([pricePredictionsFinalQueryKey]);
    queryClient.invalidateQueries(["property_share", token]);
  }, [token, queryClient]);

  const handleSubmit = () => {
    if (isSubmitDisabled) {
      showToastError("error");
      return;
    }
    qSetSimpleQuickPropertyValue.mutate(
      { price_area: price / property.area, propertyToken: token },
      {
        onSuccess: () => {
          invalidateQueries();
        },
        onError: (error) => {
          showToastError(getErrorMessage(error));
        },
      }
    );
  };

  useEffect(() => {
    // invalidated data too early - out of sync data. Happens 1/20 times
    if (
      qSetSimpleQuickPropertyValue.isSuccess &&
      property?.propertyRequestStatus !== PropertyRequestStatus.Completed
    ) {
      invalidateQueries();
    }
  }, [
    invalidateQueries,
    qSetSimpleQuickPropertyValue.isSuccess,
    property?.propertyRequestStatus,
  ]);

  useEffect(() => {
    // quick_prediction route: we valuate in the bottom of the page,
    // refreshed content appears on the top. Thus scroll to the top on unmount.
    // assumption: this component will not be rendered after refetching data due to conditional rendering.
    return () => window.scrollTo(0, 0);
  }, []);

  if (!property) return null;

  if (
    property?.propertyRequestStatus === PropertyRequestStatus.Completed &&
    !token
  ) {
    return <PredictionOverviewCard canEdit={false} property={property} />;
  }

  const cardProps: Parameters<typeof ContentCard>[0] = {
    title: isSticky ? "" : "Galima turto vertė", // Empty str --> less height taken
    containerStyle: isSticky
      ? {
          position: "sticky",
          bottom: 0,
          paddingBottom: 0,
          paddingTop: 0,
          borderTop: `1.5px solid ${colors.grey}`,
        }
      : undefined,
  };

  if (qSetSimpleQuickPropertyValue.isSuccess && token) {
    return (
      <ContentCard {...cardProps}>
        <Typography>
          {isSticky
            ? `Galima turto vertė: įvertinta €${price}. Atnaujinama ataskaita ...`
            : `Įvertinta €${price}. Atnaujinama ataskaita ...`}
        </Typography>
      </ContentCard>
    );
  }

  return (
    <ContentCard {...cardProps}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Stack direction="column" gap={2}>
          <TextField
            label="Galima turto vertė"
            id="price"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">€</InputAdornment>
              ),
            }}
            value={price ?? ""}
            onChange={(e) => {
              const value = e.target.value ? parseFloat(e.target.value) : NaN;
              if (!isNaN(value) && value > 0) {
                setPrice(value);
              } else if (e.target.value === "") {
                setPrice(null);
              }
            }}
            type="number"
            disabled={
              qSetSimpleQuickPropertyValue.isLoading ||
              qSetSimpleQuickPropertyValue.isSuccess
            }
          />
          <Stack direction="row" gap={1} alignItems="center">
            <Checkbox
              checked={isAgreed}
              onChange={(e) => {
                setIsAgreed(e.target.checked);
              }}
            />
            <Typography>
              Turto vertė yra galutinė, po išsaugojimo nebegalėsite jos keisti.
            </Typography>
          </Stack>
        </Stack>
        <Button
          variant="contained"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          Patvirtinti ir išsaugoti
        </Button>
      </Stack>
    </ContentCard>
  );
};
